import React, { useMemo } from "react";
import { Box, Chip } from "@mui/material";

const formatDurationNoSeconds = (startIso, endIso) => {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (isNaN(start) || isNaN(end)) return "";
  const totalMinutes = Math.floor((end - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes} min` : `${minutes} min`;
};

const formatMinutes = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

const AwakeTimeChip = ({
  lastSleep,
  lastFeeding,
  activeSleep,
  sleepsData,
  feedingsData,
}) => {
  // Calculate average sleep patterns (same logic as SleepTimeline)
  const averageSleepPatterns = useMemo(() => {
    if (!sleepsData || sleepsData.length === 0) return [];

    const now = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(now.getDate() - 7);

    const filteredSleeps = sleepsData.filter((sleep) => {
      const sleepDate = new Date(sleep.start);
      return sleepDate >= cutoffDate && sleep.end;
    });

    const sleepsByHour = {};
    filteredSleeps.forEach((sleep) => {
      const startDate = new Date(sleep.start);
      const startHour = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const startTimeInMinutes = startHour * 60 + startMinute;

      if (!sleepsByHour[startHour]) {
        sleepsByHour[startHour] = [];
      }
      sleepsByHour[startHour].push({ startTimeInMinutes });
    });

    const clusters = [];
    const hours = Object.keys(sleepsByHour)
      .map(Number)
      .sort((a, b) => a - b);

    hours.forEach((hour) => {
      const sleeps = sleepsByHour[hour];
      const avgStartMinutes =
        sleeps.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
        sleeps.length;

      let addedToCluster = false;
      for (let cluster of clusters) {
        const timeDiff = Math.abs(cluster.startTimeInMinutes - avgStartMinutes);
        if (timeDiff <= 120) {
          cluster.sleeps.push(...sleeps);
          cluster.count += sleeps.length;
          cluster.startTimeInMinutes =
            cluster.sleeps.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
            cluster.count;
          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusters.push({
          startTimeInMinutes: avgStartMinutes,
          count: sleeps.length,
          sleeps: sleeps,
        });
      }
    });

    return clusters
      .filter((cluster) => cluster.count >= 5)
      .map((cluster) => ({ startTimeInMinutes: cluster.startTimeInMinutes }))
      .sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
  }, [sleepsData]);

  // Calculate average feeding patterns (same logic as FeedingTimeline)
  const averageFeedingPatterns = useMemo(() => {
    if (!feedingsData || feedingsData.length === 0) return [];

    const now = new Date();
    const cutoffDate = new Date();
    cutoffDate.setDate(now.getDate() - 7);

    const filteredFeedings = feedingsData.filter((feeding) => {
      const feedingDate = new Date(feeding.start);
      return feedingDate >= cutoffDate && feeding.end;
    });

    const feedingsByHour = {};
    filteredFeedings.forEach((feeding) => {
      const startDate = new Date(feeding.start);
      const startHour = startDate.getHours();
      const startMinute = startDate.getMinutes();
      const startTimeInMinutes = startHour * 60 + startMinute;

      if (!feedingsByHour[startHour]) {
        feedingsByHour[startHour] = [];
      }
      feedingsByHour[startHour].push({ startTimeInMinutes });
    });

    const clusters = [];
    const hours = Object.keys(feedingsByHour)
      .map(Number)
      .sort((a, b) => a - b);

    hours.forEach((hour) => {
      const feedings = feedingsByHour[hour];
      const avgStartMinutes =
        feedings.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
        feedings.length;

      let addedToCluster = false;
      for (let cluster of clusters) {
        const timeDiff = Math.abs(cluster.startTimeInMinutes - avgStartMinutes);
        if (timeDiff <= 120) {
          cluster.feedings.push(...feedings);
          cluster.count += feedings.length;
          cluster.startTimeInMinutes =
            cluster.feedings.reduce((sum, s) => sum + s.startTimeInMinutes, 0) /
            cluster.count;
          addedToCluster = true;
          break;
        }
      }

      if (!addedToCluster) {
        clusters.push({
          startTimeInMinutes: avgStartMinutes,
          count: feedings.length,
          feedings: feedings,
        });
      }
    });

    return clusters
      .filter((cluster) => cluster.count >= 5)
      .map((cluster) => ({ startTimeInMinutes: cluster.startTimeInMinutes }))
      .sort((a, b) => a.startTimeInMinutes - b.startTimeInMinutes);
  }, [feedingsData]);

  if (
    activeSleep !== null ||
    sleepsData.length === 0 ||
    feedingsData.length === 0
  ) {
    return null;
  }

  // Find next expected sleep based on patterns
  const now = new Date();
  const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();

  const nextSleepPattern = averageSleepPatterns.find(
    (pattern) => pattern.startTimeInMinutes > currentTimeInMinutes
  );
  const timeUntilNextSleep = nextSleepPattern
    ? nextSleepPattern.startTimeInMinutes - currentTimeInMinutes
    : averageSleepPatterns.length > 0
    ? 1440 - currentTimeInMinutes + averageSleepPatterns[0].startTimeInMinutes
    : 0;

  // Find next expected feeding based on patterns
  const nextFeedingPattern = averageFeedingPatterns.find(
    (pattern) => pattern.startTimeInMinutes > currentTimeInMinutes
  );
  const timeUntilNextFeeding = nextFeedingPattern
    ? nextFeedingPattern.startTimeInMinutes - currentTimeInMinutes
    : averageFeedingPatterns.length > 0
    ? 1440 - currentTimeInMinutes + averageFeedingPatterns[0].startTimeInMinutes
    : 0;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 84,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
      }}
    >
      <Chip
        label={
          "😇 " +
          formatDurationNoSeconds(lastSleep?.end, new Date().toISOString()) +
          (timeUntilNextSleep > 0
            ? " (" + formatMinutes(timeUntilNextSleep) + ")"
            : "") +
          ", 🍼 " +
          formatDurationNoSeconds(lastFeeding?.end, new Date().toISOString()) +
          (timeUntilNextFeeding > 0
            ? " (" + formatMinutes(timeUntilNextFeeding) + ")"
            : "")
        }
        size="small"
        sx={{
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "#7a2d36" : "#f1828d",
          color: "white",
        }}
      />
    </Box>
  );
};

export default AwakeTimeChip;
